import React from 'react';
import PropTypes from 'prop-types';
import Clearpay, { ClearpayType } from '@deporvillage/clearpay-component';
import Notification, { NotificationType } from '@deporvillage/notification-component';
import PayPalWidget from '@deporvillage/paypal-widget-component';
import Tooltip, { TooltipPosition } from '@deporvillage/tooltip-component';
import { AplazameExtended } from '@deporvillage/aplazame-component';
import { Config, I18n, I18nContext, formatLongDate, StockLabel, getStockByStockLabel } from '@deporvillage/utils-library-component';
import Related from './Related.js';
import Variant, { VariantUsizyClick } from './Variant.js';
import WishListShare, { WishListShareClick } from './WishListShare.js';
import Klarna from './Klarna';

import styles from './ProductActions.module.scss';

const StockCopy = ({ stockLabel, range }) => {

    const { translate } = React.useContext(I18nContext);
    const { lang } = React.useContext(Config);

    let stocklbl = '';
    let timelbl = '';
    let delivery24h = stockLabel !== StockLabel.NoStock || !range;

    if (delivery24h) {
        if (stockLabel === StockLabel.TwoStock || stockLabel === StockLabel.ThreeStock) {
            stocklbl = `${ translate('product_page.stock.items_available', { stock: getStockByStockLabel(stockLabel) }) } | `;
            timelbl = translate('product_page.stock.in_stock');
        } else if (stockLabel === StockLabel.OneStock) {
            stocklbl = `${ translate('product_page.stock.item_available') } | `;
            timelbl = translate('product_page.stock.in_stock');
        } else {
            stocklbl = translate('product_page.stock.in_stock');
        }
    }

    if (!range) timelbl += ` ${ translate('product_page.delivery.day') }`;
    else timelbl += ` ${ translate('product_page.delivery.days', {
        from: formatLongDate(range.from, lang),
        to: formatLongDate(range.to, lang),
    }) }`;

    return (
        <p className={ styles['tooltip-text'] }>
            <span className={ `${ stockLabel === StockLabel.FullStock || stockLabel === StockLabel.NoStock && !range ? styles['special'] : '' }` }> { stocklbl } </span>
            <span className={ `${ delivery24h ? styles['special'] : '' }` }> { timelbl } </span>
        </p>
    );
};

StockCopy.propTypes = {
    stockLabel: PropTypes.string,
    range: PropTypes.shape({
        from: PropTypes.string,
        to: PropTypes.string,
    }),
};

const showApalazame = ({ enabled, allowedCountries, min, max }, country, price) => {
    if (enabled && allowedCountries.indexOf(country) !== -1 && price >= min && price <= max) return true;
    else return false;
};

const showClearPay = ({enabled, min, max, includeCategories, excludeCategories}, ids, price) => {
    const e = (ids || []).filter(c => (excludeCategories || []).includes(c));
    const i = (ids || []).filter(c => (includeCategories || []).includes(c));
    if (!enabled || price < min || price > max || e.length && !i.length) return false;
    else return true;
};

const ProductActions = ({ product, clickCart, clickVariant, clickWishList, clickShare, clickRelated, isWishListActive, isAdded, isValidCurrency }) => {

    const { id, title, gallery_images: gallery, variants, outOfStock, bike_mount: bikeMount, is_exclusive: isExclusive,
        related_products: relatedProducts, default_variant: defaultVariant, fit, url: productUrl, non_returnable,
        size_recommendation: recommendation, sku, size_guide_image: sizeGuide, delivery, promotion, categoryIds, promotionRequested,
    } = product;

    const reference = React.useRef(null);
    const tooltipRef = React.useRef(null);

    const { translate } = React.useContext(I18nContext);
    const { url, aplazame, klarna, clearPay, paypal, country } = React.useContext(Config);

    const [showError, setShowError] = React.useState(false);
    const [selectedOption, setSelectedOption] = React.useState(null);
    const [isTooltipVisible, setIsTooltipVisible] = React.useState(false);

    const varianData = { fit, recommendation, price: defaultVariant.price, sku, sizeGuide };
    const relatedProduct = { id, name: title, image: gallery.images[gallery.default] };

    // istanbul ignore next
    const showSelectSize = variants.length && variants[0].select_size && !selectedOption && defaultVariant.stockLabel !== StockLabel.NoStock ? true : null;

    const scrollToComponent = () => reference.current.scrollIntoView({ behavior: 'smooth' });
    const handleToggleTooltip = () => setIsTooltipVisible(!isTooltipVisible);

    const handleOutsideClick = (e) => {
        if (tooltipRef.current && tooltipRef.current.contains(e.target)) return;
        setIsTooltipVisible(false);
    };

    const handleClickCart = () => {
        if (variants.length && !selectedOption) {
            setShowError(true);
            scrollToComponent();
            return;
        }
        clickCart({ selectedOption, product });
    };

    const handleClickRelated = () => clickRelated && clickRelated();

    const changeOptionSelector = (obj) => {
        setSelectedOption(obj);
        setShowError(false);
        clickVariant && clickVariant(null, obj);
    };

    const getDeliveryMessageTooltip = () => {
        if (delivery.stockLabel !== StockLabel.NoStock && !delivery.range)
            return `${ translate('product_page.delivery.tooltip_day') } ${ translate('product_page.delivery.bank_transfer') }`;
        return `${ translate('product_page.delivery.tooltip_days') } ${ translate('product_page.delivery.bank_transfer') }`;
    };

    const handleClickWishListShare = (ev) => {
        switch (ev) {
            case WishListShareClick.Share:
                clickShare && clickShare();
                break;
            case WishListShareClick.WishList:
                clickWishList && clickWishList({ selectedOption, product });
                break;
        }
    };

    React.useEffect(() => {
        /* eslint-disable no-undef */
        document.addEventListener('click', handleOutsideClick);
        return () => { document.removeEventListener('click', handleOutsideClick); };
        /* eslint-enable no-undef */
    }, []);

    return (
        <div className={ styles['product-actions-component'] } ref={ reference }>

            { promotion ?
                <div className={ styles['promotion-wrapper'] }>
                    <i className={ `${ styles['icon'] } icon icon-plant-tree` }/> <I18n>product_page.promotion.plant_tree</I18n>
                </div>: null }

            { (relatedProducts || []).map(related => (
                <div key={ related.name } className={ `${ styles['product-action'] } ${ styles['product-action-related'] }` }>
                    <Related
                        type={ related.name }
                        options={ [relatedProduct, ...related.items] }
                        onClick={ handleClickRelated }
                    />
                </div>
            )) }

            {variants.map(variant => (
                <div key={variant.id} className={`${styles['product-action']} ${styles['product-action-variant']}`}>
                    <Variant change={changeOptionSelector}
                        name={variant.name}
                        options={variant.items} placeholder={<I18n>product_page.sizing.choose_option</I18n>}
                        onClick={clickVariant || null}
                        {...varianData}
                    />
                </div>
            ))}

            { showError || showSelectSize ? (
                <div className={ styles['validation-error'] }>
                    <I18n>product_page.sizing.select_a_size</I18n>
                </div> ) : null }

            <div className={ `${ styles['product-action'] } ${ styles['product-action-tooltip'] } ${ variants.length ? styles['product-action-tooltip-options'] : '' }` }>
                <div className={ styles['tooltip-text-section'] } onClick={ handleToggleTooltip } data-testid="tooltip-container" ref={ tooltipRef }>

                    { delivery ? <StockCopy stockLabel={ delivery.stockLabel } range={ delivery.range }/> : null}

                    { delivery && isTooltipVisible ?
                        <div className={ styles['tooltip-wrapper'] }>
                            <Tooltip position={ TooltipPosition.Below } content={
                                <div className={ styles['tooltip-content'] }>
                                    { getDeliveryMessageTooltip() }
                                </div>
                            }/>
                        </div> : null
                    }
                </div>
            </div>

            <div className={`${styles['product-action']} ${styles['product-atc']}`}>
            {
                    outOfStock ? (
                        <Notification
                            type={NotificationType.Info}
                            message={translate('product_page.stock.out_of_stock')}
                        />
                    ) : (
                        <button
                            onClick={handleClickCart}
                            className={`${styles['checkout-cart-button']} ${isAdded ? styles['added'] : ''}`}
                        >
                            {
                                isAdded ? (
                                    <>
                                        <i className={`${styles['icon']} icon icon-done`} />
                                        <span>
                                            <I18n>product_page.actions.added_to_cart</I18n>
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <i className={`${styles['icon']} icon icon-add-shopping-cart`} />
                                        <span>
                                            <I18n>product_page.actions.add_to_cart</I18n>
                                        </span>
                                    </>
                                )
                            }
                        </button>
                    )
                }
            </div>

            {/* No Mostrar aplazame cuando coincide el precio min y max con clearpay (80 - 800); clearpay tiene preferencia */}
            { !outOfStock && aplazame && showApalazame(aplazame, country.code, defaultVariant.price.price) &&
                !(clearPay && showClearPay(clearPay, categoryIds, defaultVariant.price.price)) ? (
                <div className={ `${ styles['product-action'] } ${ styles['product-finance-simulator'] }` }>
                    <AplazameExtended
                        config={aplazame}
                        price={defaultVariant.price.price}
                    />
                </div>
            ): null }

            { !outOfStock && clearPay && showClearPay(clearPay, categoryIds, defaultVariant.price.price) ? (
                <div className={ `${ styles['product-action'] } ${ styles['product-clearpay'] }` }>
                    <Clearpay
                        type={ClearpayType.Product}
                        config={clearPay}
                        price= {defaultVariant.price.price}
                    />
                </div>
            ) : null }

            {/* TODO Remove this block when the clearpay promotion is over since it is shown above in the parent component */}
            {
                !promotion && paypal?.widget.enabled && isValidCurrency &&
                !outOfStock && clearPay && showClearPay(clearPay, categoryIds, defaultVariant.price.price) ? (
                    <div className={`${styles['product-action']} ${ styles['product-paypal'] }`}>
                        {promotionRequested && <PayPalWidget amount={defaultVariant.price.price} />}
                    </div>
                ) : null
            }

            { !outOfStock && klarna ? (
                <div className={ styles['product-action'] }>
                    <Klarna { ...klarna }/>
                </div>
            ) : null }

            <div className={ styles['product-action'] }>
                <WishListShare onClick={ handleClickWishListShare }
                               link={ `${ url }${ productUrl }` }
                               active={ isWishListActive }
                />
            </div>

            <div className={ styles['product-action-copies'] }>
                <div className={ styles['product-action-copy'] }>
                    <i className={ `${ styles['icon'] } icon icon-tag` }/> <I18n>product_page.title.best_price</I18n>
                </div>
                <div className={ styles['product-action-copy'] }>
                    <span className={ `${ styles['icon'] } icon icon-refresh` }/>
                    { non_returnable ? <I18n>product_page.title.non_returnable</I18n> : <I18n>product_page.title.returns</I18n> }
                </div>
                { isExclusive ?
                    <div className={ styles['product-action-copy'] }>
                        <span className={ `${ styles['icon'] } icon icon-star-o` }/> <I18n>product_page.title.is_exclusive</I18n>
                    </div> : null }
                { bikeMount ?
                    <div className={ styles['product-action-copy'] }>
                        <span className={ `${ styles['icon'] } icon icon-tools` }/> <I18n>product_page.title.bike_mount</I18n>
                    </div> : null }
            </div>

        </div>
    );
};

ProductActions.propTypes = {
    product: PropTypes.object,
    clickCart: PropTypes.func,
    clickVariant: PropTypes.func,
    clickWishList: PropTypes.func,
    clickShare: PropTypes.func,
    clickRelated: PropTypes.func,
    isWishListActive: PropTypes.bool,
    isAdded: PropTypes.bool,
    isValidCurrency: PropTypes.bool
};

export default ProductActions;
export { VariantUsizyClick as ProductActionVariantClick };
